import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  PopupWheelContent,
  PopupWheelSegment,
} from "@/shared/types/popup-types";
import type { ResolvedStepDesign } from "@/shared/types/popup-design";
import { fontStack } from "./engine";
import {
  WHEEL_DEFAULTS,
  autoLabelFontSize,
  contrastText,
  pointerAngle,
  polar,
  rotationFromMatrix,
  segmentAtRotation,
  segmentFill,
  segmentIsWin,
  segmentSpan,
  targetRotation,
  wedgePath,
} from "@/lib/wheel";
import { burstConfetti } from "@/lib/confetti";

/** The stage's lifecycle, DRIVEN BY THE PARENT (the widget owns the network):
 *  idle → spinning (submit fired, outcome unknown: free spin) → result (the
 *  server named a wedge: decelerate onto it) → landed. `error` = the submit
 *  failed mid-spin: coast to a stop, then back to idle. */
export type WheelStatus = "idle" | "spinning" | "result" | "error";

interface WheelStageProps {
  wheel: PopupWheelContent;
  /** Normalized wedges (lib/wheel normalizeSegments) — the array the server
   *  indexes into, so `resultIndex` maps onto exactly these. */
  segments: PopupWheelSegment[];
  step: ResolvedStepDesign;
  panelIsLight: boolean;
  status: WheelStatus;
  /** Wedge to land on. Read only while status === "result". */
  resultIndex: number | null;
  /** Fires exactly once per spin, when the wheel has come to rest on
   *  `resultIndex`. */
  onLanded?: (index: number) => void;
  /** Fires once the wheel has coasted to a stop after an `error` status. */
  onSettled?: () => void;
  /** Static preview: paints the wheel, never animates. */
  inert?: boolean;
}

const VB = 400; // SVG viewBox size
const CX = VB / 2;
const CY = VB / 2;

/** Free-spin (outcome unknown) lap time is derived from the deceleration so
 *  the hand-off is seamless: the ease-out curve's INITIAL slope is 1.2× its
 *  average speed, so the cruise runs at that speed — no visible speed-up or
 *  hitch when the result arrives. */
const DECEL_BEZIER = "cubic-bezier(0.25, 0.3, 0.12, 1)";
const DECEL_INITIAL_SLOPE = 1.2;

const reducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const STAGE_CSS = `
@keyframes fp-wheel-idle {
  0%, 100% { transform: rotate(var(--fp-wheel-from, 0deg)); }
  50% { transform: rotate(calc(var(--fp-wheel-from, 0deg) + 4deg)); }
}
@keyframes fp-wheel-kick {
  from { transform: rotate(var(--fp-wheel-from, 0deg)); }
  to { transform: rotate(calc(var(--fp-wheel-from, 0deg) + 360deg)); }
}
@keyframes fp-wheel-free {
  from { transform: rotate(var(--fp-wheel-from, 0deg)); }
  to { transform: rotate(calc(var(--fp-wheel-from, 0deg) + 360deg)); }
}
@keyframes fp-wheel-pointer-tap {
  0% { transform: translateX(-50%) rotate(0deg); }
  40% { transform: translateX(-50%) rotate(-14deg); }
  100% { transform: translateX(-50%) rotate(0deg); }
}
.fp-wheel-idle { animation: fp-wheel-idle 3.2s ease-in-out infinite; }
@media (max-width: 767px) {
  .flowapt-wheel.fp-wheel-mw { width: min(78vw, var(--fp-wheel-m)) !important; }
}
@media (prefers-reduced-motion: reduce) {
  .fp-wheel-idle { animation: none !important; }
}
`;

/**
 * The wheel itself: an SVG disc that the parent drives through
 * idle → spinning → result → landed. Everything visual is configurable via
 * PopupWheelContent; everything about the OUTCOME is decided upstream (the
 * server), this component only animates to the wedge it is told.
 */
export const WheelStage: React.FC<WheelStageProps> = ({
  wheel,
  segments,
  step,
  panelIsLight,
  status,
  resultIndex,
  onLanded,
  onSettled,
  inert = false,
}) => {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const discRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [interacted, setInteracted] = useState(false);
  const [pointerTap, setPointerTap] = useState(0);
  // The last wedge boundary the pointer crossed — drives the peg tick.
  const lastWedgeRef = useRef<number>(-1);
  const rafRef = useRef<number | null>(null);
  const audioRef = useRef<AudioContext | null>(null);
  const landedRef = useRef(false);
  const fallbackTimerRef = useRef<number | null>(null);

  const count = segments.length;
  const span = segmentSpan(count);
  const size = wheel.size && wheel.size > 0 ? wheel.size : WHEEL_DEFAULTS.size;
  // Phones (<768px): an optional smaller wheel, still capped at 78vw.
  const mobileSize =
    typeof wheel.mobile_size === "number" && wheel.mobile_size >= 120 && wheel.mobile_size < size
      ? Math.round(wheel.mobile_size)
      : 0;
  const borderWidth =
    typeof wheel.border_width === "number" && wheel.border_width >= 0
      ? wheel.border_width
      : WHEEL_DEFAULTS.border_width;
  const pointer = wheel.pointer_position || "top";
  const spinMs =
    wheel.spin_duration_ms && wheel.spin_duration_ms >= 1500
      ? wheel.spin_duration_ms
      : WHEEL_DEFAULTS.spin_duration_ms;
  const minRotations =
    typeof wheel.min_rotations === "number" && wheel.min_rotations >= 1
      ? Math.min(wheel.min_rotations, 20)
      : WHEEL_DEFAULTS.min_rotations;
  const rim = wheel.border_color || (panelIsLight ? "#ffffff" : "#1f2937");
  const pegColor = wheel.peg_color || (panelIsLight ? "#ffffff" : "#e5e7eb");
  const pointerColor = wheel.pointer_color || (panelIsLight ? "#111827" : "#ffffff");
  const hubColor = wheel.hub_color || (panelIsLight ? "#ffffff" : "#111827");
  const hubTextColor = wheel.hub_text_color || contrastText(hubColor);

  const R = CX - Math.max(2, borderWidth / 2) - 2; // wedge radius inside the rim
  const hubStyle = wheel.hub_style || "ring";
  const hubPct =
    typeof wheel.hub_size === "number" && wheel.hub_size >= 4 && wheel.hub_size <= 60
      ? wheel.hub_size
      : 18;
  const HUB_R = hubStyle === "none" ? 0 : Math.round((VB * hubPct) / 200);
  const pointerStyle_ = wheel.pointer_style === "badge" ? "badge" : "pin";
  const pointerIcon = (wheel.pointer_icon || "★").trim() || "★";
  const pointerIconColor = wheel.pointer_icon_color || "#facc15";
  const wedgeDivider = wheel.wedge_divider !== false;

  const longestLabel = useMemo(
    () => segments.reduce((m, s) => Math.max(m, s.label.length), 0),
    [segments]
  );
  const fontSize =
    wheel.label_font_size && wheel.label_font_size > 0
      ? wheel.label_font_size
      : autoLabelFontSize(count, longestLabel);

  // ---- sound (opt-in): one short click per wedge boundary ----
  const tick = useCallback(() => {
    if (wheel.sound !== true) return;
    try {
      const Ctx =
        (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      if (!audioRef.current) audioRef.current = new Ctx();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") void ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.09, ctx.currentTime + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.05);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.06);
    } catch {
      /* audio is decoration — never let it break the spin */
    }
  }, [wheel.sound]);

  // Watch the live rotation while the wheel moves: peg ticks + pointer tap.
  const startWatching = useCallback(() => {
    if (rafRef.current !== null) return;
    const loop = () => {
      const el = discRef.current;
      if (el) {
        const deg = rotationFromMatrix(getComputedStyle(el).transform);
        const wedge = segmentAtRotation(deg, count, pointer);
        if (wedge !== lastWedgeRef.current) {
          lastWedgeRef.current = wedge;
          tick();
          setPointerTap((n) => n + 1);
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [count, pointer, tick]);

  const stopWatching = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    stopWatching();
    if (fallbackTimerRef.current) window.clearTimeout(fallbackTimerRef.current);
    audioRef.current?.close?.().catch(() => {});
  }, [stopWatching]);

  /** Current visual rotation, read from the compositor so an interrupted
   *  animation hands its exact angle to the next phase. */
  const currentRotation = (): number => {
    const el = discRef.current;
    if (!el) return 0;
    return rotationFromMatrix(getComputedStyle(el).transform);
  };

  /** Freeze the disc at its current angle (kills any running animation)
   *  and return that angle, so a transition can pick up from it. */
  const freeze = (): number => {
    const el = discRef.current;
    if (!el) return 0;
    const deg = currentRotation();
    el.style.animation = "none";
    el.style.transition = "none";
    el.style.transform = `rotate(${deg}deg)`;
    // Force the frozen frame to commit before the next transform transitions
    // from it — otherwise the browser coalesces both writes into one jump.
    void el.offsetWidth;
    return deg;
  };

  // ---- phase: spinning (outcome unknown) ----
  useEffect(() => {
    if (inert || status !== "spinning") return;
    const el = discRef.current;
    if (!el) return;
    setInteracted(true);
    landedRef.current = false;
    const from = freeze();
    if (reducedMotion()) return; // hold still; the result phase does a short settle
    const avgDegPerMs = (360 * (minRotations + 0.5)) / spinMs;
    const lapMs = Math.max(350, Math.round(360 / (avgDegPerMs * DECEL_INITIAL_SLOPE)));
    el.style.setProperty("--fp-wheel-from", `${from}deg`);
    // One eased kick-off lap, then constant-speed laps until the result lands.
    el.style.animation = `fp-wheel-kick ${Math.round(lapMs * 1.6)}ms cubic-bezier(0.45, 0, 1, 0.55) 1, fp-wheel-free ${lapMs}ms linear ${Math.round(lapMs * 1.6)}ms infinite`;
    lastWedgeRef.current = segmentAtRotation(from, count, pointer);
    startWatching();
    return () => {
      /* the next phase freezes + re-targets; nothing to undo here */
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, inert]);

  // ---- phase: result (decelerate onto the wedge) ----
  useEffect(() => {
    if (inert || status !== "result" || resultIndex === null) return;
    const el = discRef.current;
    if (!el || landedRef.current) return;
    const from = freeze();
    const rm = reducedMotion();
    const jitter = (Math.random() - 0.5) * 0.84;
    const target = targetRotation(
      from,
      Math.max(0, Math.min(count - 1, resultIndex)),
      count,
      rm ? 0 : minRotations,
      pointer,
      jitter
    );
    const duration = rm ? 600 : spinMs;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      landedRef.current = true;
      stopWatching();
      el.removeEventListener("transitionend", onEnd);
      if (fallbackTimerRef.current) window.clearTimeout(fallbackTimerRef.current);
      const seg = segments[resultIndex];
      const win = !!seg && segmentIsWin(seg);
      if (wheel.haptics !== false && typeof navigator !== "undefined" && navigator.vibrate) {
        try {
          navigator.vibrate(win ? [40, 60, 40] : 30);
        } catch {
          /* unsupported */
        }
      }
      if (win && wheel.confetti !== false && stageRef.current) {
        const root = stageRef.current.getRootNode();
        const box = stageRef.current.getBoundingClientRect();
        burstConfetti(
          (root instanceof ShadowRoot ? root : document.body) as ParentNode & Node,
          { x: box.left + box.width / 2, y: box.top + box.height / 2 }
        );
      }
      onLanded?.(resultIndex);
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.target !== el) return;
      if (e.propertyName && e.propertyName !== "transform") return;
      finish();
    };
    el.addEventListener("transitionend", onEnd);
    el.style.transition = `transform ${duration}ms ${DECEL_BEZIER}`;
    el.style.transform = `rotate(${target}deg)`;
    if (!rm) startWatching();
    // transitionend never fires in a background tab — a fallback timer
    // guarantees the flow still advances.
    fallbackTimerRef.current = window.setTimeout(finish, duration + 250);
    return () => {
      el.removeEventListener("transitionend", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, resultIndex, inert]);

  // ---- phase: error (coast to a stop, hand control back) ----
  useEffect(() => {
    if (inert || status !== "error") return;
    const el = discRef.current;
    if (!el) return;
    const from = freeze();
    const duration = reducedMotion() ? 200 : 900;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      stopWatching();
      el.removeEventListener("transitionend", onEnd);
      if (fallbackTimerRef.current) window.clearTimeout(fallbackTimerRef.current);
      onSettled?.();
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.target !== el) return;
      if (e.propertyName && e.propertyName !== "transform") return;
      finish();
    };
    el.addEventListener("transitionend", onEnd);
    el.style.transition = `transform ${duration}ms cubic-bezier(0.2, 0.6, 0.3, 1)`;
    el.style.transform = `rotate(${from + 140}deg)`;
    fallbackTimerRef.current = window.setTimeout(finish, duration + 250);
    return () => el.removeEventListener("transitionend", onEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, inert]);

  // ---- phase: idle (reset for the next spin) ----
  useEffect(() => {
    if (status !== "idle") return;
    landedRef.current = false;
    stopWatching();
    const el = discRef.current;
    if (!el) return;
    // Keep whatever angle the wheel rests at; only drop the transition so
    // the idle wobble (a CSS animation) can take over cleanly.
    const deg = currentRotation();
    el.style.transition = "none";
    el.style.animation = "";
    el.style.transform = "";
    el.style.setProperty("--fp-wheel-from", `${deg}deg`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (count === 0) return null;

  const idleWobble =
    !inert &&
    status === "idle" &&
    wheel.idle_wobble !== false &&
    !interacted &&
    !reducedMotion();

  // Radial room for a label: from just outside the hub to just inside the
  // rim. A wedge with an icon gives the icon the outer band and pulls its
  // label inward so the two never overlap.
  const labelAvail = R * 0.84 - Math.max(HUB_R, 18) - 14;
  const ICON_BAND = Math.round(fontSize * 1.9);

  // Badge pointer sits ON the rim (its centre on the wheel's edge); the pin
  // hangs just outside it.
  const badgeStyle: React.CSSProperties =
    pointer === "right"
      ? { right: "-6.5%", top: "50%", transform: "translateY(-50%)" }
      : pointer === "left"
        ? { left: "-6.5%", top: "50%", transform: "translateY(-50%) rotate(180deg)" }
        : { left: "50%", top: "-6.5%", transform: "translateX(-50%) rotate(90deg)" };
  const pointerStyle: React.CSSProperties =
    pointer === "right"
      ? {
          right: "-4.5%",
          top: "50%",
          transform: "translateY(-50%) rotate(90deg)",
          transformOrigin: "center",
        }
      : pointer === "left"
        ? {
            left: "-4.5%",
            top: "50%",
            transform: "translateY(-50%) rotate(-90deg)",
            transformOrigin: "center",
          }
        : {
            left: "50%",
            top: "-5.5%",
            transform: "translateX(-50%)",
            transformOrigin: "50% 20%",
            animation:
              pointerTap > 0 && status !== "idle" && !reducedMotion()
                ? "fp-wheel-pointer-tap 140ms ease-out"
                : undefined,
          };

  return (
    <div
      ref={stageRef}
      className={mobileSize ? "flowapt-wheel fp-wheel-mw" : "flowapt-wheel"}
      data-status={status}
      style={{
        position: "relative",
        width: `min(78vw, ${size}px)`,
        ...(mobileSize ? { ["--fp-wheel-m" as string]: `${mobileSize}px` } : {}),
        aspectRatio: "1 / 1",
        margin: "0 auto",
        fontFamily: fontStack(step.theme.fontFamily),
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    >
      <style>{STAGE_CSS}</style>

      {wheel.glow === true && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: "-8%",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${wheel.glow_color || "rgba(250,204,21,0.55)"} 0%, rgba(0,0,0,0) 68%)`,
            filter: "blur(6px)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* The rotating disc. Transform is written imperatively by the phase
          effects (the compositor is the source of truth mid-spin). */}
      <div
        ref={discRef}
        className={idleWobble ? "fp-wheel-idle" : undefined}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          willChange: "transform",
          boxShadow: `0 18px 40px -16px rgba(0,0,0,0.55)`,
        }}
      >
        <svg
          viewBox={`0 0 ${VB} ${VB}`}
          width="100%"
          height="100%"
          role="img"
          aria-label={`Prize wheel with ${count} prizes: ${segments.map((s) => s.label).join(", ")}`}
          style={{ display: "block", overflow: "visible" }}
        >
          <defs>
            <radialGradient id={`fp-wheel-shine-${uid}`} cx="50%" cy="38%" r="65%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22" />
              <stop offset="70%" stopColor="#ffffff" stopOpacity="0" />
            </radialGradient>
            <clipPath id={`fp-wheel-hub-${uid}`}>
              <circle cx={CX} cy={CY} r={Math.max(0, HUB_R - 6)} />
            </clipPath>
          </defs>

          {segments.map((seg, i) => {
            const start = i * span;
            const end = start + span;
            const mid = start + span / 2;
            const fill = segmentFill(seg, i, wheel.palette);
            const color = seg.text_color?.trim() || contrastText(fill);
            const hasIcon = !!(seg.icon && seg.icon.trim());
            const avail = hasIcon ? labelAvail - ICON_BAND : labelAvail;
            const estWidth = seg.label.length * fontSize * 0.58;
            const squeeze = estWidth > avail ? { textLength: avail, lengthAdjust: "spacingAndGlyphs" as const } : {};
            const labelR = Math.max(HUB_R, 18) + 12 + avail / 2;
            return (
              <g key={seg.id}>
                <path
                  d={wedgePath(CX, CY, R, start, end)}
                  fill={fill}
                  stroke={wedgeDivider ? rim : fill}
                  strokeWidth={count > 1 ? 1.5 : 0}
                  strokeLinejoin="round"
                />
                <g transform={`rotate(${mid} ${CX} ${CY})`}>
                  {hasIcon && (
                    <text
                      x={CX}
                      y={CY - (R - 8 - ICON_BAND / 2)}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={Math.round(fontSize * 1.3)}
                      transform={`rotate(-90 ${CX} ${CY - (R - 8 - ICON_BAND / 2)})`}
                      style={{ pointerEvents: "none" }}
                    >
                      {seg.icon!.trim()}
                    </text>
                  )}
                  <text
                    x={CX}
                    y={CY - labelR}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={color}
                    fontSize={fontSize}
                    fontWeight={700}
                    letterSpacing="0.02em"
                    transform={`rotate(-90 ${CX} ${CY - labelR})`}
                    style={{ pointerEvents: "none" }}
                    {...squeeze}
                  >
                    {seg.label}
                  </text>
                </g>
              </g>
            );
          })}

          {/* depth: a soft highlight across the upper half of the disc */}
          <circle cx={CX} cy={CY} r={R} fill={`url(#fp-wheel-shine-${uid})`} pointerEvents="none" />

          {/* rim */}
          {borderWidth > 0 && (
            <circle
              cx={CX}
              cy={CY}
              r={CX - borderWidth / 2 - 1}
              fill="none"
              stroke={rim}
              strokeWidth={borderWidth}
            />
          )}

          {/* pegs at every wedge boundary */}
          {wheel.pegs !== false &&
            segments.map((seg, i) => {
              const [px, py] = polar(CX, CY, CX - borderWidth / 2 - 1, i * span);
              return (
                <circle
                  key={`peg-${seg.id}`}
                  cx={px}
                  cy={py}
                  r={Math.max(3, Math.min(6, borderWidth * 0.55))}
                  fill={pegColor}
                  stroke="rgba(0,0,0,0.18)"
                  strokeWidth={1}
                />
              );
            })}

          {/* hub */}
          {hubStyle !== "none" && (
            <circle
              cx={CX}
              cy={CY}
              r={HUB_R}
              fill={hubColor}
              stroke={hubStyle === "plain" ? "none" : "rgba(0,0,0,0.18)"}
              strokeWidth={hubStyle === "plain" ? 0 : 2}
            />
          )}
          {hubStyle === "ring" && (
            <circle
              cx={CX}
              cy={CY}
              r={HUB_R - 7}
              fill="none"
              stroke={hubTextColor}
              strokeOpacity={0.25}
              strokeWidth={1.5}
            />
          )}
          {hubStyle === "none" ? null : wheel.hub_logo_url ? (
            <image
              href={wheel.hub_logo_url}
              x={CX - (HUB_R - 6)}
              y={CY - (HUB_R - 6)}
              width={(HUB_R - 6) * 2}
              height={(HUB_R - 6) * 2}
              preserveAspectRatio="xMidYMid slice"
              clipPath={`url(#fp-wheel-hub-${uid})`}
            />
          ) : wheel.hub_text ? (
            <text
              x={CX}
              y={CY}
              textAnchor="middle"
              dominantBaseline="central"
              fill={hubTextColor}
              fontSize={wheel.hub_text.length > 5 ? 11 : 14}
              fontWeight={800}
              letterSpacing="0.12em"
              style={{ pointerEvents: "none" }}
            >
              {wheel.hub_text.toUpperCase()}
            </text>
          ) : hubStyle === "ring" ? (
            <circle cx={CX} cy={CY} r={6} fill={hubTextColor} fillOpacity={0.55} />
          ) : null}
        </svg>
      </div>

      {/* pointer — sits OUTSIDE the rotating disc */}
      {pointerStyle_ === "badge" ? (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            width: "13%",
            height: "13%",
            pointerEvents: "none",
            filter: "drop-shadow(0 3px 5px rgba(0,0,0,0.3))",
            ...badgeStyle,
          }}
        >
          {/* drawn pointing LEFT (into the wheel from the right rim); the
              other positions rotate the whole badge */}
          <svg viewBox="0 0 60 60" width="100%" height="100%" style={{ display: "block", overflow: "visible" }}>
            <path
              d="M2 30 L14 19 L14 41 Z"
              fill={pointerColor}
              stroke="rgba(0,0,0,0.12)"
              strokeWidth="1"
              strokeLinejoin="round"
            />
            <circle cx="32" cy="30" r="26" fill={pointerColor} stroke="rgba(0,0,0,0.12)" strokeWidth="1" />
            <text
              x="32"
              y="31"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="26"
              fill={pointerIconColor}
              style={{ fontWeight: 700 }}
            >
              {pointerIcon}
            </text>
          </svg>
        </div>
      ) : (
      <div
        aria-hidden="true"
        key={pointerTap}
        style={{
          position: "absolute",
          width: "11%",
          height: "13%",
          pointerEvents: "none",
          filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.35))",
          ...pointerStyle,
        }}
      >
        <svg viewBox="0 0 44 52" width="100%" height="100%" style={{ display: "block" }}>
          <path
            d="M22 51 L4 20 A18 18 0 1 1 40 20 Z"
            fill={pointerColor}
            stroke="rgba(0,0,0,0.25)"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <circle cx="22" cy="19" r="6" fill={contrastText(pointerColor)} fillOpacity="0.85" />
        </svg>
      </div>
      )}
    </div>
  );
};

export default WheelStage;
